import { useMemo, useSyncExternalStore } from "react";
import { safeLocalStorage } from "@/lib/storage";

export type ExperimentToggle = "off" | "on";

export interface ChatExperiments {
  taskSections: ExperimentToggle;
  calloutCodesmithIcons: ExperimentToggle;
}

export type ChatExperimentKey = keyof ChatExperiments;

export interface ChatExperimentDescriptor {
  key: ChatExperimentKey;
  label: string;
  value: ExperimentToggle;
  defaultValue: ExperimentToggle;
}

const STORAGE_KEY = "codesmith-chat-experiments";
const EXPERIMENTS: {
  [K in ChatExperimentKey]: {
    label: string;
    defaultValue: ChatExperiments[K];
  };
} = {
  taskSections: { label: "Task-named sections", defaultValue: "off" },
  calloutCodesmithIcons: {
    label: "Animated callout icons",
    defaultValue: "off",
  },
};
const DEFAULT_SNAPSHOT = Object.fromEntries(
  Object.entries(EXPERIMENTS).map(([key, experiment]) => [
    key,
    experiment.defaultValue,
  ]),
) as unknown as ChatExperiments;
const listeners = new Set<() => void>();

let snapshot: ChatExperiments | null = null;

export function isChatExperimentKey(
  value: unknown,
): value is ChatExperimentKey {
  return typeof value === "string" && Object.hasOwn(EXPERIMENTS, value);
}

export function experimentLabel<K extends ChatExperimentKey>(key: K): string {
  return EXPERIMENTS[key].label;
}

function readStorage(): ChatExperiments {
  const raw = safeLocalStorage.get(STORAGE_KEY);
  if (!raw) return DEFAULT_SNAPSHOT;
  try {
    const stored = JSON.parse(raw) as Partial<
      Record<keyof ChatExperiments, unknown>
    >;
    const keys = Object.keys(EXPERIMENTS) as ChatExperimentKey[];
    return Object.fromEntries(
      keys.map((key) => [
        key,
        stored[key] === "on" || stored[key] === "off"
          ? stored[key]
          : EXPERIMENTS[key].defaultValue,
      ]),
    ) as unknown as ChatExperiments;
  } catch {
    return DEFAULT_SNAPSHOT;
  }
}

function getSnapshot(): ChatExperiments {
  if (snapshot === null) snapshot = readStorage();
  return snapshot;
}

function notify(): void {
  for (const listener of listeners) listener();
}

// Picks up writes from other documents on this origin, such as the dashboard
// framed in the extension panel flipping an experiment for this tab.
function onStorage(event: StorageEvent): void {
  if (event.key !== null && event.key !== STORAGE_KEY) return;
  snapshot = null;
  notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener("storage", onStorage);
      // Writes made while nobody listens are never replayed.
      snapshot = null;
    }
  };
}

export function experimentDefault<K extends ChatExperimentKey>(
  key: K,
): ChatExperiments[K] {
  return EXPERIMENTS[key].defaultValue;
}

export function setExperiment<K extends ChatExperimentKey>(
  key: K,
  value: ChatExperiments[K],
): void {
  snapshot = { ...getSnapshot(), [key]: value };
  safeLocalStorage.set(STORAGE_KEY, JSON.stringify(snapshot));
  notify();
}

export function useExperiment<K extends ChatExperimentKey>(
  key: K,
): ChatExperiments[K] {
  return useSyncExternalStore(
    subscribe,
    () => getSnapshot()[key],
    () => EXPERIMENTS[key].defaultValue,
  );
}

export function useChatExperimentDescriptors(): ChatExperimentDescriptor[] {
  const current = useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => DEFAULT_SNAPSHOT,
  );
  return useMemo(
    () =>
      (Object.keys(EXPERIMENTS) as ChatExperimentKey[]).map((key) => ({
        key,
        label: EXPERIMENTS[key].label,
        value: current[key],
        defaultValue: EXPERIMENTS[key].defaultValue,
      })),
    [current],
  );
}
